import { FormDateTime, FormInput, FormState, Labeled } from 'component_utils/FormUtils'
import VirtualizedList from 'components/BeTable/VirtualizedList'
import ProcessingButton from 'components/ProcessingButton'
import RoleProtected from 'components/RoleProtected'
import useResizeDetector from 'components/useResizeDetector'
import { ISimpleUserDetails, IUserTask, IUserTask$ITimesWorked } from 'interfaces'
import React, { CSSProperties, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FaClock, FaEdit, FaFlagCheckered, FaShareAlt, FaSync, FaTrash } from 'react-icons/fa'
import { Button, ButtonGroup, Card, CardBody, CardFooter, CardHeader, Collapse, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap'
import { authToSimpleUser, useApi, useApiLoader, useAuth } from 'utils/API'
import Translation from 'utils/Language/Translation'
import { GoProjectTemplate } from 'react-icons/go'
import { addPrompt, choice, confirm, prompt } from 'utils/Prompts'
import { entries, fromPairs, sortBy, sumBy } from 'lodash'
import ExpandedIndicator from 'components/ExpandedIndicator'
import If from 'components/If'
import { ScannerFocusTrigger } from 'components/Scanner'
import Raw from 'components/Raw'
import Date, { DATE_TIME } from 'components/Date'
import TemplateEditor from './TaskTemplateEditor'
import TaskInitializer from './TaskInitializer'
import SimpleModal from 'components/SimpleModal'
import moment from 'moment'
import { BiCabinet } from 'react-icons/bi'
import { beTableAsComponent } from 'components/BeTable'
import { mapNotNil, retryForOptimisticLocking } from 'component_utils/utils'
import TimerImage from '../../../stopwatch.gif';
import classNames from 'classnames'
import { useTranslations } from 'utils/language'

const OverviewTable = beTableAsComponent<IUserTask & { usernames: string[] }, { reload: any; users: ISimpleUserDetails[] }>({
  tableId: 'table#user_task_overview',
  columns: [
    {
      Header: 'ID',
      value: row => row.id
    },
    {
      Header: 'Users',
      value: row => row.usernames.join(", "),
      filterable: true,
    },
    {
      Header: 'Title',
      value: row => row.title,
      filterable: true,
    },
    {
      Header: 'Time',
      Cell: ({ row, reload, users }) => {
        const minutes = sumBy(row.timesWorked, tw => (
          moment.duration(
            (tw.finished ? moment(tw.finished) : moment()).diff(moment(tw.started))
          ).asMinutes()
        ))

        return (
          <>
            <TimeView entry={row} reload={reload} onlyMine={false} users={users} />
            {' '}
            {
              !isNaN(minutes) &&
              minutes !== null && <Translation name="T.misc.minutes" params={{ minutes: minutes?.toFixed(0) }} />
            }
          </>
        )
      }
    },
    {
      Header: '',
      width: 60,
      fixedWidth: true,
      resizable: false,
      Cell: ({ row, reload }) => {
        const api = useApi()
        return (
          <ProcessingButton block color='danger' className='py-0' onClick={async () => {
            if (await confirm(<Translation name="T.misc.areYouSure?"/>)) {
              await api.userTasks.deleteUserTask(row.id)
              reload()
            }
          }}>
            <FaTrash/>
          </ProcessingButton>
        )
      }
    },
  ]
})

const Overview: FC<{ users: ISimpleUserDetails[] }> = ({ users }) => {
  const api = useApi()
  const { data, isLoading, reload } = useApiLoader(
    useCallback(async () => {
      const d = await api.userTasks.getAllTasks()

      return d.map(it => ({
        ...it,
        usernames: mapNotNil(it.users, (uc) => {
          return users.find(it => it.userid === uc)?.username
        })
      }))
    }, [api, users]),
    [],
    false
  )
  return (
    <SimpleModal
      title=''
      fullscreen
      onOpen={reload}
      trigger={open => (
        <Button onClick={open}>
          <BiCabinet/>
        </Button>
      )}
    >
      <OverviewTable
        loading={isLoading}
        data={data}
        getExtraProps={() => ({
          reload,
          users
        })}
      />
    </SimpleModal>
  )
}


const paramPrompt = (params: string[]): Promise<{[key: string] :string}> => {
  if (params.length === 0) {
    return Promise.resolve({})
  }

  const defaultValues = fromPairs(params.map(it => {
    return [it, '']
  }))
  return addPrompt(({ index, id, resolve, afterClosed }) => {
    const [state, setState] = useState(defaultValues);
    const [isOpen, setIsOpen] = useState(true);

    const cancel = useCallback(() => {
      resolve(null);
      setIsOpen(false);
    }, [resolve]);

    const ok = useCallback(() => {
      resolve(state);
      setIsOpen(false);
    }, [resolve, state]);

    return (
      <Modal
        onClick={(e) => {
          e.stopPropagation();
        }}
        isOpen={isOpen}
        onOpened={() => document.getElementById(`prompts-${id}-${params[0]}`).focus()}
        onClosed={afterClosed}
      >
        <ScannerFocusTrigger/>
        <ModalHeader>
          <Translation name="T.prompts.prompt" />
        </ModalHeader>

        <ModalBody>
          <FormState state={state} setState={setState} idPrefix={`prompts-${id}`}>
            {params.map(it => {
              return (
                <Labeled key={it} title={it}>
                  <FormInput path={it}/>
                </Labeled>
              )
            })}
          </FormState>
        </ModalBody>

        <ModalFooter>
          <Button
            id={`prompts-${id}-btn-cancel`}
            className="w-25"
            color="secondary"
            onClick={cancel}
          >
            <Translation name="T.misc.cancel" />
          </Button>
          <Button
            id={`prompts-${id}-btn-ok`}
            autoFocus
            className="w-25"
            color="primary"
            onClick={ok}
          >
            <Translation name="T.misc.ok" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  });
}

const TimeViewEditor: FC<{ user: ISimpleUserDetails; trigger: any; getBase: () => IUserTask$ITimesWorked; save: any; }> = ({ user, trigger, getBase, save }) => {
  const [state, setState] = useState({})
  return (
    <SimpleModal
      title=""
      trigger={trigger}
      onOpen={() => {
        const base = getBase()
        setState({
          ...base, 
          started: base.started ? moment(base.started).toDate() : null,
          finished: base.finished ? moment(base.finished).toDate() : null,
        })
      }}
      buttons={close => [
        <ProcessingButton color='primary' onClick={async () => {
          await save(state)
          close()
        }}>
          <Translation name="T.misc.save"/>
        </ProcessingButton>
      ]}
    >
      <FormState state={state} setState={setState}>
        <Labeled title={<Translation name="T.objects.userTasks.workedTimes.message"/>}>
          <FormInput path='message'/>
        </Labeled>
        <Labeled title={<Translation name="T.objects.userTasks.workedTimes.started"/>}>
          <FormDateTime path='started'/>
        </Labeled>
        <Labeled title={<Translation name="T.objects.userTasks.workedTimes.finished"/>}>
          <FormDateTime path='finished' allowClear/>
        </Labeled>
      </FormState>
    </SimpleModal>
  )
}

const TimeView: FC<{ entry: IUserTask, reload: any; onlyMine: boolean; users: ISimpleUserDetails[] }> = ({ entry, reload, onlyMine, users }) => {
  const api = useApi()
  const auth = useAuth()

  const currentUserWorkEntry = entry.timesWorked.find(it => it.userId === auth.userId && it.finished === null)

  const showEntries = onlyMine ? entry.timesWorked.filter(it => it.userId === auth.userId) : entry.timesWorked

  return (
    <SimpleModal
      title={<Translation name="T.objects.userTasks.workedTimes.workedTimesFor" params={(entry || {}) as any}/>}
      trigger={open => (
        <ProcessingButton className='py-0 mb-2' onClick={open}>
          <FaClock/>
        </ProcessingButton>
      )}
      buttons={close => [
        <TimeViewEditor
          user={authToSimpleUser(auth)}
          trigger={(open: any) => (
            <ProcessingButton onClick={open}>
              <Translation name='T.misc.new'/>
            </ProcessingButton>  
          )}
          getBase={() => ({
            id: null,
            version: 0,
            userId: auth.userId,
            message: '',
            started: null,
            finished: null,
          })}
          save={async (tw: IUserTask$ITimesWorked) => {
            await retryForOptimisticLocking(3, async () => {
              const currEntry = await api.userTasks.getTask(entry.id)
              currEntry.timesWorked.push(tw)
              await api.userTasks.saveUserTask(currEntry)
            })
          }}
        />,
        currentUserWorkEntry ? (
          <ProcessingButton color='danger' onClick={async () => {
            const msg = await prompt(<Translation name="T.objects.userTasks.workedTimes.describeWhatWorkYouDid"/>, '', true)
            if (msg === null) {
              return;
            }

            await retryForOptimisticLocking(3, async () => {
              const currEntry = await api.userTasks.getTask(entry.id)
              currEntry.timesWorked.forEach(it => {
                if (it.userId === auth.userId && it.finished === null) {
                  it.finished = moment().toDate()
                  it.message = msg
                }
              })
              await api.userTasks.saveUserTask(currEntry)
            })
            reload()
          }}>
            <Translation name="T.misc.finish"/>
          </ProcessingButton>
        ) : (
          <ProcessingButton color='success' onClick={async () => {
            await retryForOptimisticLocking(3, async () => {
              const currEntry = await api.userTasks.getTask(entry.id)
              currEntry.timesWorked.push({
                id: null,
                version: 0,
                userId: auth.userId,
                message: '',
                started: moment().toDate(),
                finished: null
              })
              await api.userTasks.saveUserTask(currEntry)
            })
            reload()
          }}>
            <Translation name="T.misc.start"/>
          </ProcessingButton>
        )
      ]}
    >
      <Table striped>
        <tbody>
          {showEntries.map(tw => {
            let minutes = null;
            minutes = moment.duration(
              (tw.finished ? moment(tw.finished) : moment()).diff(moment(tw.started))
            ).asMinutes();
            return (
              <tr key={tw.id}>
                <td className='w-100'>
                  {!onlyMine && (
                    <b>
                      {users.find(it => it.userid === tw.userId)?.username ?? ""}:{' '}
                    </b>
                  )}
                  {tw.message && <><b>{tw.message}</b><br/></>}
                  Start: <Date val={tw.started} format={DATE_TIME}/><br/>
                  Stopped: {tw.finished && <Date val={tw.finished} format={DATE_TIME}/>}<br/>
                  Minutes: {
                    !isNaN(minutes) &&
                    minutes !== null && <Translation name="T.misc.minutes" params={{ minutes: minutes?.toFixed(0) }} />
                  }<br/>
                </td>
                <td>
                  <TimeViewEditor
                    user={users.find(it => it.userid === tw.userId)}
                    trigger={(open: any) => (
                      <ProcessingButton className='mb-2 py-0' onClick={open}>
                        <FaEdit/>
                      </ProcessingButton>  
                    )}
                    getBase={() => tw}
                    save={async (tw: IUserTask$ITimesWorked) => {
                      await retryForOptimisticLocking(3, async () => {
                        const currEntry = await api.userTasks.getTask(entry.id)
                        const idx = currEntry.timesWorked.findIndex(it => it.id === tw.id)
                        currEntry.timesWorked[idx] = tw
                        await api.userTasks.saveUserTask(currEntry)
                      })
                    }}
                  />
                  <br/>
                  <ProcessingButton color='danger' className='py-0' onClick={async () => {
                    if (!(await confirm(<Translation name="T.misc.areYouSure?"/>))) {
                      return
                    }

                    await retryForOptimisticLocking(3, async () => {
                      const currEntry = await api.userTasks.getTask(entry.id)
                      currEntry.timesWorked = currEntry.timesWorked.filter(it => it.id !== tw.id)
                      await api.userTasks.saveUserTask(currEntry)
                    })
                    reload()                    
                  }}>
                    <FaTrash/>
                  </ProcessingButton>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </SimpleModal>
  )
}

const Entry: FC<{ entry: IUserTask, reload: any }> = ({ entry, reload }) => {
  const [isOpen, setIsOpen] = useState(false)
  const api = useApi()
  const auth = useAuth()

  const currentUserWorkEntry = entry.timesWorked.find(it => it.userId === auth.userId && it.finished === null)

  const hasContent = entry.text || (entry.buttons.length > 0)

  return (
    <>
      <Card outline style={{
        borderColor: entry.borderColor ? entry.borderColor : undefined
      }}>
        <CardHeader className='d-flex flex-row align-items-center' onClick={() => setIsOpen(old => hasContent && !old)}>
          {hasContent && <ExpandedIndicator isExpanded={isOpen}/>}
          {entry.users.length > 1 && (
            <FaShareAlt className='mr-1'/>
          )}
          {currentUserWorkEntry && (
            <img alt="timer indicator" className={classNames('mr-1 react-icons')} style={{ width: 'unset' }} src={TimerImage} />
          )}
          <div className="flex-fill">
            {entry.title}<br/>
            <Date val={entry.createdAt} format={DATE_TIME}/>
          </div>
          <div className='mr-1' onClick={e => {
            e.stopPropagation()
          }}>
            <TimeView entry={entry} reload={reload} onlyMine users={[]}/>
            <br/>
            <ProcessingButton className='py-0' onClick={async () => {
              if (await confirm(<Translation name="T.misc.areYouSure?"/>)) {
                await api.userTasks.markFinished(entry.id)
              }
            }}>
              <FaFlagCheckered/>
            </ProcessingButton>
          </div>
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <If cond={entry.text}>
            <CardBody>
              <Raw>{entry.text}</Raw>
            </CardBody>
          </If>
          <If cond={entry.buttons.length > 0}>
            <CardFooter className='text-right'>
              {entry.buttons.map(b => (
                <ProcessingButton className='ml-1' color={b.color} onClick={async () => {
                  await api.userTasks.buttonPressed(
                    entry.id, 
                    b.id, 
                    await paramPrompt(b.params)
                  )
                }}>
                  {b.title}
                </ProcessingButton>
              ))}
            </CardFooter>
          </If>
        </Collapse>
      </Card>
    </>
  )
}

const listeners: any[] = []
export let refreshCurrentUserTasks = () => {
  listeners.forEach(it => it())
}

export default () => {
  const { data: templates, reload: reloadTemplates, lazyLoad: lazyLoadTemplates } = useApiLoader(useCallback(api => api.userTasks.templates.get(), []), [], false)
  const { data: users, lazyLoad: lazyLoadUsers } = useApiLoader(useCallback(api => api.users.getSimpleUsers(), []), [], false)
  const T = useTranslations()
  const { data, reload } = useApiLoader(
    useCallback(
      async api => sortBy(await api.userTasks.getCurrentTasks(), it => it.priority * -1), []
    ),
    [],
    true
  )
  const { height, ref: sizeRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: false,
    refreshMode: 'debounce',
    refreshRate: 500,
  });

  useEffect(() => {
    listeners.push(reload)
    return () => {
      listeners.splice(listeners.indexOf(reload), 1);
    }
  }, [reload])

  const [filterState, setFilterState] = useState({
    title: '',
    activeOnly: true
  })

  const filteredTasks = useMemo(() => {
    let res = data
    if (filterState.activeOnly) {
      res = data.filter(it => it.finishedAt === null)
    }
    if (filterState.title.trim()) {
      res = data.filter(it => it.title.includes(filterState.title.trim()))
    }
    return res
  }, [data, filterState])

  return (
    <div className='flex-container'>
      <FormState state={filterState} setState={setFilterState}>
        <FormInput path='title'/>
      </FormState>
      <div className={'nested-flex-container my-2'} ref={sizeRef}>
        <VirtualizedList
          containerProps={{
            style: {
              overflowY: 'scroll',
              height: `${height}px`,
              width: '100%',
            } as CSSProperties,
          }}
          padRenderer={({ height }) => (
            <div
              style={{
                width: `100%`,
                height: `${height}px`,
                ...(height === 0 ? { display: 'none' } : {}),
              }}
            />
          )}
          maxHeight={height}
          overscan={0}
          minRowSize={30}
          rowCount={filteredTasks.length}
          rowRenderer={({ index, props }) => {
            const entry = filteredTasks[index];
            return (
              <div key={index} {...props} className='pb-2'>
                <Entry reload={reload} entry={entry}/>
              </div>
            );
          }}
        />
      </div>
      <ButtonGroup className='w-100'>
        <RoleProtected roles={['userTaskManager']} onHasRole={useCallback(() => {
          lazyLoadUsers()
          lazyLoadTemplates()
        }, [lazyLoadUsers, lazyLoadTemplates])}>
          <TaskInitializer
            users={users}
            reloadTasks={reload}
            reloadTemplates={reloadTemplates}
            trigger={(open: any) => (
              <ProcessingButton
                disabled={templates.length === 0} 
                block 
                onClick={async () => {
                  const res = await choice(
                    <Translation name="T.objects.userTasks.whichTemplate"/>, 
                    '' + templates[0].id, 
                    templates.map(t => ([''+t.id, t.title]))
                  )
                  if (res) {
                    const template = templates.find(it => (''+it.id) === res)
                    open(template)
                  }
                }}
              >
                <Translation name="T.misc.new"/>
              </ProcessingButton>      
            )}      
          />
          <TemplateEditor
            users={users}
            reloadTasks={reload}
            reloadTemplates={reloadTemplates}
            trigger={(open: any) => (
              <ProcessingButton color='tertiary' onClick={async () => {
                if (templates.length === 0) {
                  open({}, [], null)
                  return 
                }
                
                const res = await choice(
                  <Translation name="T.objects.userTasks.whichTemplate"/>, 
                  '', 
                  [
                    ['', T('T.misc.new')],
                    ...templates.map(t => ([''+t.id, t.title]))
                  ]
                )

                if (res === '') {
                  open({}, [], null)
                } else if (res) {
                  const template = templates.find(it => (''+it.id) === res)
                  open({                   
                    priority: template.priority,
                    borderColor: template.borderColor,
                    title: template.title,
                    text: template.text,
        
                    parameters: entries(template.parameters).map(it => ({ name: it[0], value: it[1] })),
                    buttons: template.buttons.map(it => ({
                      id: it.id,
                      color: it.color,
                      title: it.title,
                      params: it.params.join(",")
                    }))
                  }, template.users.map(it => ''+it), template)
                }
              }}>
                <GoProjectTemplate/>
              </ProcessingButton>
            )}
          />
          <Overview users={users}/>
        </RoleProtected>
        <ProcessingButton color='info' onClick={reload}>
          <FaSync/>
        </ProcessingButton>
      </ButtonGroup>
    </div>
  )
}